import type { ResolvedTemplateDesign, SiteTemplateDesign, TemplateCssVars } from "./types";
export { siteBuilderButtonClass as buttonClass } from "./siteBuilderTheme";

export const siteThemeColorRoles = [
    "primary",
    "accent",
    "background",
    "surface",
    "text",
    "muted",
    "border",
    "onPrimary",
    "onAccent",
    "onBackground",
    "onSurface",
    "inverse",
    "onInverse",
    "primarySoft",
    "accentSoft",
    "surfaceElevated",
    "borderStrong",
    "overlay",
    "shadowColor",
    "focusRing",
] as const;

export type SiteThemeColorRole = typeof siteThemeColorRoles[number];

const siteThemeColorVariables: Record<SiteThemeColorRole, `--site-${string}`> = {
    primary: "--site-primary",
    accent: "--site-accent",
    background: "--site-background",
    surface: "--site-surface",
    text: "--site-text",
    muted: "--site-muted",
    border: "--site-border",
    onPrimary: "--site-on-primary",
    onAccent: "--site-on-accent",
    onBackground: "--site-on-background",
    onSurface: "--site-on-surface",
    inverse: "--site-inverse",
    onInverse: "--site-on-inverse",
    primarySoft: "--site-primary-soft",
    accentSoft: "--site-accent-soft",
    surfaceElevated: "--site-surface-elevated",
    borderStrong: "--site-border-strong",
    overlay: "--site-overlay",
    shadowColor: "--site-shadow-color",
    focusRing: "--site-focus-ring",
};

export function siteThemeColor(role: SiteThemeColorRole): `var(--site-${string})` {
    return `var(${siteThemeColorVariables[role]})`;
}

export const defaultTemplateDesign: ResolvedTemplateDesign = {
    colors: {
        primary: "#2563eb",
        accent: "#f59e0b",
        background: "#ffffff",
        surface: "#f8fafc",
        text: "#0f172a",
        muted: "#64748b",
        border: "#e2e8f0",
    },
    typography: {
        heading: "Inter",
        body: "Inter",
    },
    layout: {
        container: "1180px",
        sectionSpacing: "comfortable",
        radius: "10px",
    },
    components: {
        header: "classic",
        footer: "columns",
        buttons: "solid",
        forms: "card",
        cartFlow: "multiStep",
    },
};

export function mergeTemplateDesign(
    base?: SiteTemplateDesign | null,
    override?: SiteTemplateDesign | null,
): ResolvedTemplateDesign {
    return {
        colors: {
            ...defaultTemplateDesign.colors,
            ...(base?.colors ?? {}),
            ...(override?.colors ?? {}),
        },
        typography: {
            ...defaultTemplateDesign.typography,
            ...(base?.typography ?? {}),
            ...(override?.typography ?? {}),
        },
        layout: {
            ...defaultTemplateDesign.layout,
            ...(base?.layout ?? {}),
            ...(override?.layout ?? {}),
        },
        components: {
            ...defaultTemplateDesign.components,
            ...(base?.components ?? {}),
            ...(override?.components ?? {}),
        },
    };
}

type RgbColor = {
    blue: number;
    green: number;
    red: number;
};

const templateFontVariableByFamily: Record<string, `--font-${string}`> = {
    "barlow condensed": "--font-barlow-condensed",
    "cormorant garamond": "--font-cormorant",
    "eb garamond": "--font-eb-garamond",
    inter: "--font-inter",
    manrope: "--font-manrope",
    "roboto slab": "--font-roboto-slab",
    sora: "--font-sora",
    "space grotesk": "--font-space-grotesk",
};

function templateFontFamily(fontFamily: string): string {
    const leadingFamily = fontFamily.split(",", 1)[0]?.trim() ?? "";
    const normalizedFamily = leadingFamily
        .replace(/^(['"])(.*)\1$/, "$2")
        .toLowerCase();
    const variable = templateFontVariableByFamily[normalizedFamily];

    if (!variable) {
        return fontFamily;
    }

    return `var(${variable}, ${leadingFamily}), ${fontFamily}`;
}

function parseHexColor(value: string): RgbColor | null {
    const match = value.trim().match(/^#([\da-f]{3}|[\da-f]{6}|[\da-f]{8})$/i);

    if (!match) {
        return null;
    }

    const hex = match[1].length === 3
        ? match[1].split("").map((character) => `${character}${character}`).join("")
        : match[1].slice(0, 6);

    return {
        red: Number.parseInt(hex.slice(0, 2), 16),
        green: Number.parseInt(hex.slice(2, 4), 16),
        blue: Number.parseInt(hex.slice(4, 6), 16),
    };
}

function parseRgbColor(value: string): RgbColor | null {
    const match = value.trim().match(/^rgba?\(\s*([\d.]+)%?\s*[, ]\s*([\d.]+)%?\s*[, ]\s*([\d.]+)%?(?:\s*[,/]\s*[\d.]+%?)?\s*\)$/i);

    if (!match) {
        return null;
    }

    const percentage = value.includes("%");
    const component = (raw: string) => Math.max(0, Math.min(255, Number(raw) * (percentage ? 2.55 : 1)));

    return {
        red: component(match[1]),
        green: component(match[2]),
        blue: component(match[3]),
    };
}

function relativeLuminance(color: RgbColor): number {
    const component = (value: number) => {
        const normalized = value / 255;

        return normalized <= 0.04045
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    };

    return (0.2126 * component(color.red))
        + (0.7152 * component(color.green))
        + (0.0722 * component(color.blue));
}

function contrastRatio(left: number, right: number): number {
    const lighter = Math.max(left, right);
    const darker = Math.min(left, right);

    return (lighter + 0.05) / (darker + 0.05);
}

function contrastTextColor(background: string, colors: ResolvedTemplateDesign["colors"]): string {
    const parsed = parseHexColor(background) ?? parseRgbColor(background);

    if (!parsed) {
        return colors.text;
    }

    const backgroundLuminance = relativeLuminance(parsed);
    const candidates = [colors.text, colors.background, colors.surface, colors.primary, colors.accent]
        .filter((value, index, values) => values.indexOf(value) === index);
    const bestPaletteColor = candidates.reduce((best, candidate) => {
        const candidateColor = parseHexColor(candidate) ?? parseRgbColor(candidate);
        const bestColor = parseHexColor(best) ?? parseRgbColor(best);

        if (!candidateColor) return best;
        if (!bestColor) return candidate;

        return contrastRatio(backgroundLuminance, relativeLuminance(candidateColor))
            > contrastRatio(backgroundLuminance, relativeLuminance(bestColor))
            ? candidate
            : best;
    }, candidates[0] ?? colors.text);
    const parsedPaletteColor = parseHexColor(bestPaletteColor) ?? parseRgbColor(bestPaletteColor);

    if (
        parsedPaletteColor
        && contrastRatio(backgroundLuminance, relativeLuminance(parsedPaletteColor)) >= 4.5
    ) {
        return bestPaletteColor;
    }

    return contrastRatio(backgroundLuminance, relativeLuminance({ red: 0, green: 0, blue: 0 }))
        >= contrastRatio(backgroundLuminance, relativeLuminance({ red: 255, green: 255, blue: 255 }))
        ? "#000000"
        : "#ffffff";
}

export function templateDesignToCssVars(design: ResolvedTemplateDesign): TemplateCssVars {
    const { colors } = design;

    return {
        "--site-primary": colors.primary,
        "--site-accent": colors.accent,
        "--site-background": colors.background,
        "--site-surface": colors.surface,
        "--site-text": colors.text,
        "--site-muted": colors.muted,
        "--site-border": colors.border,
        "--site-on-primary": contrastTextColor(colors.primary, colors),
        "--site-on-accent": contrastTextColor(colors.accent, colors),
        "--site-on-background": contrastTextColor(colors.background, colors),
        "--site-on-surface": contrastTextColor(colors.surface, colors),
        "--site-inverse": colors.text,
        "--site-on-inverse": contrastTextColor(colors.text, colors),
        "--site-primary-soft": `color-mix(in srgb, ${colors.primary} 14%, ${colors.background})`,
        "--site-accent-soft": `color-mix(in srgb, ${colors.accent} 16%, ${colors.background})`,
        "--site-surface-elevated": `color-mix(in srgb, ${colors.surface} 92%, ${colors.background})`,
        "--site-border-strong": `color-mix(in srgb, ${colors.border} 72%, ${colors.text})`,
        "--site-overlay": `color-mix(in srgb, ${colors.text} 48%, transparent)`,
        "--site-shadow-color": `color-mix(in srgb, ${colors.text} 18%, transparent)`,
        "--site-focus-ring": colors.primary,
        "--site-container": design.layout.container,
        "--site-radius": design.layout.radius,
        "--site-heading-font": templateFontFamily(design.typography.heading),
        "--site-body-font": templateFontFamily(design.typography.body),
    };
}

export function sectionPaddingClass(spacing: string): string {
    if (spacing === "spacious") return "py-20";
    if (spacing === "calm") return "py-10";

    return "py-14";
}
